import { axiosClient } from "@/shared/api/axios-client";
import type { Paginado } from "@/lib/types";

/**
 * Catálogo "clave producto/servicio SAT → cuenta contable" (equivalente de
 * dbo.Tag) · deriva la cuenta contable del prorrateo FB60 a partir de la
 * clave SAT del concepto del CFDI. Paginado server-side (P42).
 */
export interface ClaveSatClaveCuenta {
  id: string;
  claveSat: string;
  claveSatDescripcion: string | null;
  cuentaContableId: string;
  cuentaCodigo: string;
  cuentaNombre: string;
  nombre: string;
  activo: boolean;
}

export interface CrearClaveSatClaveCuentaInput {
  claveSat: string;
  cuentaContableId: string;
  nombre: string;
}

export type EditarClaveSatClaveCuentaInput = CrearClaveSatClaveCuentaInput;

export interface ListarClavesSatClavesCuentaParams {
  busqueda?: string;
  page?: number;
  pageSize?: number;
}

/** Fila cruda del Excel de importación · clave SAT + CÓDIGO de la cuenta (no id). */
export interface FilaImportacionClaveSatClaveCuenta {
  claveSat: string;
  cuentaCodigo: string;
  nombre: string;
}

/** Detalle por fila del preview de importación · qué se hará con cada renglón. */
export interface PreviewFilaClaveSatClaveCuenta {
  renglon: number;
  claveSat: string;
  cuentaCodigo: string;
  nombre: string | null;
  estado: "Crear" | "Actualizar" | "Omitir";
  motivo: string | null;
}

/** Resumen devuelto por el endpoint de importación masiva (real y dry-run). */
export interface ResultadoImportacionClaveSatClaveCuenta {
  creados: number;
  actualizados: number;
  omitidos: number;
  errores: string[];
  detalle: PreviewFilaClaveSatClaveCuenta[];
}

/** Listado PAGINADO server-side · filtro `busqueda` (contains normalizado). */
export async function listarClavesSatClavesCuenta(
  params: ListarClavesSatClavesCuentaParams = {}
): Promise<Paginado<ClaveSatClaveCuenta>> {
  const { data } = await axiosClient.get<Paginado<ClaveSatClaveCuenta>>(
    "/api/admin/mapeos-clave-cuenta",
    { params }
  );
  return data;
}

export async function crearClaveSatClaveCuenta(
  input: CrearClaveSatClaveCuentaInput
): Promise<{ id: string }> {
  const { data } = await axiosClient.post<{ id: string }>(
    "/api/admin/mapeos-clave-cuenta",
    input
  );
  return data;
}

export async function editarClaveSatClaveCuenta(
  id: string,
  input: EditarClaveSatClaveCuentaInput
): Promise<void> {
  await axiosClient.patch(`/api/admin/mapeos-clave-cuenta/${id}`, input);
}

/** Baja lógica / reactivación · NUNCA hard delete (la cuenta deriva prorrateos históricos). */
export async function cambiarEstadoClaveSatClaveCuenta(
  id: string,
  activo: boolean
): Promise<void> {
  await axiosClient.post(`/api/admin/mapeos-clave-cuenta/${id}/estado`, { activo });
}

/**
 * Importa (o valida en dry-run) las filas. Con `soloValidar=true` el backend
 * corre la misma lógica SIN persistir y devuelve el detalle por fila para el
 * preview; con `false` persiste de verdad.
 */
export async function importarClavesSatClavesCuenta(
  filas: FilaImportacionClaveSatClaveCuenta[],
  soloValidar = false
): Promise<ResultadoImportacionClaveSatClaveCuenta> {
  const { data } = await axiosClient.post<ResultadoImportacionClaveSatClaveCuenta>(
    "/api/admin/mapeos-clave-cuenta/importar",
    { filas, soloValidar }
  );
  return data;
}

/** Resumen del borrado masivo · elimina si nada la usa, desactiva si algo la usa. */
export interface ResultadoEliminarMasivoClaveSatClaveCuenta {
  eliminados: number;
  desactivados: number;
  noEncontrados: number;
}

/**
 * Eliminación masiva: por cada mapeo seleccionado, borra permanentemente si NADA
 * lo usa en un prorrateo, o lo desactiva (baja lógica) si algún prorrateo derivó
 * su cuenta de él.
 */
export async function eliminarMasivoClavesSatClavesCuenta(
  ids: string[]
): Promise<ResultadoEliminarMasivoClaveSatClaveCuenta> {
  const { data } = await axiosClient.post<ResultadoEliminarMasivoClaveSatClaveCuenta>(
    "/api/admin/mapeos-clave-cuenta/eliminar-masivo",
    { ids }
  );
  return data;
}
